import { useEffect, useState, useMemo, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Alert, CircularProgress } from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import { PageLayout } from '../../PageLayout';
import {
  getAdviseActiveEngagementSubject,
  getAdviseEngagementLoadingSubject,
  useAdviseEngagements
} from '../../../store/AdviseEngagement';
import { getAdviseSupplierSubject, useAdviseSuppliers } from '../../../store/AdviseSupplier';
import { getAdviseCustomerSubject, useAdviseCustomers } from '../../../store/AdviseCustomer';
import { getSelectedAccountsSubject } from '../../../store/User';
import { AdviseEngagement, AdviseSupplier, AdviseCustomer } from '../../../types/advise';
import { updateAdviseEngagement } from '../../../Api/Advise/Engagements';
import { ReportLayoutEditor, ProfessionalReportPreview } from '../../Compounds/Advise/Reports';
import { buildSupplierPricingTotals } from '../../Compounds/Advise/pricing';
import { ReportSettings, ReportData, DEFAULT_SECTIONS } from '../../Compounds/Advise/Reports/types';
import { buildFeatureComparison } from '../../Compounds/Advise/Reports/featureComparison';
import { useSelectedMatrixFeatures } from '../../../Api/Advise/useSelectedMatrixFeatures';
import { useEngagementFeatureInput } from '../../../Api/Advise/useEngagementFeatureInput';
import { AdviseAccountGuard } from '../../Compounds/Advise/AdviseAccountGuard';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { canEditEngagement } from './canEditEngagement';
import { StepsLoadError, withheldReportSections } from '../../Compounds/Advise/EngagementSteps';
import { useEngagementConfiguration } from '../../../store/AdviseEngagementConfiguration';
import { LoadError } from '../../Compounds/Advise/LoadError';

export const AdviseReportEditorPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const engagementId = searchParams.get('id');

  const [engagement, setEngagement] = useState<AdviseEngagement | null>(null);
  const [suppliers, setSuppliers] = useState<AdviseSupplier[]>([]);
  const [customers, setCustomers] = useState<AdviseCustomer[]>([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState<ReportSettings>({
    sections: { ...DEFAULT_SECTIONS }
  });
  const [previewData, setPreviewData] = useState<ReportData | null>(null);
  const {
    configuration,
    failed: configurationFailed,
    retry
  } = useEngagementConfiguration(engagementId, { refresh: true });
  const matrix = useSelectedMatrixFeatures(engagementId);
  const answers = useEngagementFeatureInput(engagementId);

  const { fetchEngagementById } = useAdviseEngagements();
  const { fetchSuppliers } = useAdviseSuppliers();
  const { fetchCustomers } = useAdviseCustomers();
  const { setSnack } = useSnackbar();

  useEffect(() => {
    const subs = [
      getAdviseActiveEngagementSubject().subscribe(setEngagement),
      getAdviseEngagementLoadingSubject().subscribe(setLoading),
      getAdviseSupplierSubject().subscribe(setSuppliers),
      getAdviseCustomerSubject().subscribe(setCustomers)
    ];
    return () => subs.forEach((s) => s.unsubscribe());
  }, []);

  useEffect(() => {
    if (engagementId) fetchEngagementById(engagementId);
    fetchSuppliers();
    fetchCustomers();
  }, [engagementId]);

  useEffect(() => {
    const accountSub = getSelectedAccountsSubject().subscribe(() => {
      fetchCustomers(true);
    });
    return () => accountSub.unsubscribe();
  }, []);

  useEffect(() => {
    if (engagement?.report_settings) {
      setSettings({
        sections: {
          ...DEFAULT_SECTIONS,
          ...((engagement.report_settings.sections as Record<string, boolean>) || {})
        },
        theme: engagement.report_settings.theme as ReportSettings['theme'],
        feature_matrix: engagement.report_settings.feature_matrix as ReportSettings['feature_matrix'],
        typography: engagement.report_settings.typography as ReportSettings['typography'],
        spacing: engagement.report_settings.spacing as ReportSettings['spacing']
      });
    }
  }, [engagement?.report_settings]);

  const selectedSuppliers = useMemo(
    () => suppliers.filter((v) => engagement?.selected_suppliers?.includes(v.id)),
    [suppliers, engagement?.selected_suppliers]
  );

  const customer = useMemo(
    () => customers.find((c) => c.id === engagement?.customer_id) || null,
    [customers, engagement?.customer_id]
  );

  const handleSaveSettings = useCallback(async () => {
    if (!engagement) return;
    const { error } = await updateAdviseEngagement(engagement.id, {
      report_settings: settings as unknown as Record<string, unknown>
    });
    setSnack(
      error
        ? { message: 'Failed to save report settings. Please try again.', open: true, type: 'error' }
        : { message: 'Report settings saved.', open: true, type: 'success' }
    );
  }, [engagement, settings, setSnack]);

  const buildReportData = useCallback((): ReportData | null => {
    if (!engagement || !configuration || !matrix.features || !answers.featureInput) return null;

    const featureComparison = buildFeatureComparison(
      matrix.features,
      selectedSuppliers,
      engagement.feature_notes,
      answers.featureInput.responses
    );

    const totals = buildSupplierPricingTotals(engagement, selectedSuppliers);

    const today = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    return {
      engagement,
      suppliers: selectedSuppliers,
      customer,
      date: today,
      executiveSummary: '',
      featureComparison,
      detailedPricing: totals,
      roiAnalysis: engagement.roi_analysis || {},
      settings,
      steps: configuration.steps
    };
  }, [engagement, selectedSuppliers, customer, settings, configuration, matrix.features, answers.featureInput]);

  const handlePreview = useCallback(() => {
    const data = buildReportData();
    if (data) setPreviewData(data);
  }, [buildReportData]);

  if (!engagementId) {
    return (
      <PageLayout
        pageTitle="Report Editor"
        dataCy="advise-report-editor-page">
        <Alert severity="warning">No engagement ID provided.</Alert>
      </PageLayout>
    );
  }

  if (loading && !engagement) {
    return (
      <PageLayout
        pageTitle="Report Editor"
        dataCy="advise-report-editor-page">
        <Box className="flex justify-center p-12">
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  if (!engagement) {
    return (
      <PageLayout
        pageTitle="Report Editor"
        dataCy="advise-report-editor-page">
        <Alert severity="error">Engagement not found.</Alert>
      </PageLayout>
    );
  }

  const pageActions = (
    <Box className="flex items-center gap-2">
      <Button
        size="small"
        variant="contained"
        disabled={!configuration || !matrix.features || !answers.featureInput}
        onClick={handlePreview}>
        Preview Report
      </Button>
      {canEditEngagement(engagement, engagementId) && (
        <Button
          size="small"
          variant="outlined"
          onClick={handleSaveSettings}>
          Save Settings
        </Button>
      )}
      <Button
        size="small"
        startIcon={<BackIcon />}
        onClick={() => navigate(`/advise/analysis?id=${engagement.id}`)}>
        Back to Analysis
      </Button>
    </Box>
  );

  return (
    <PageLayout
      pageTitle={`Report Editor — ${engagement.engagement_name}`}
      pageActions={pageActions}
      dataCy="advise-report-editor-page">
      <AdviseAccountGuard>
        <Box className="flex flex-col gap-4">
          {configurationFailed && (
            <StepsLoadError
              consequence="the preview cannot leave out the ones turned off"
              onRetry={retry}
            />
          )}
          {matrix.failed && (
            <LoadError
              subject="the Functionality Matrix"
              consequence="the preview cannot list the selected features"
              onRetry={matrix.retry}
            />
          )}
          {answers.failed && (
            <LoadError
              subject="the suppliers' answers"
              consequence="the preview cannot show which suppliers support each feature"
              onRetry={answers.reload}
            />
          )}
          <Typography
            variant="body2"
            color="textSecondary">
            Configure which sections appear in the report and adjust formatting. Use Preview to see the full report.
          </Typography>

          <Box className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ReportLayoutEditor
              settings={settings}
              onChange={setSettings}
              withheldSections={withheldReportSections(configuration?.steps)}
            />
            <Box className="border rounded p-4 bg-gray-50">
              <Typography
                variant="subtitle2"
                gutterBottom>
                Report Info
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary">
                Engagement: {engagement.engagement_name}
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary">
                Customer: {customer?.company_name || '—'}
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary">
                Suppliers: {selectedSuppliers.length}
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary">
                Features: {matrix.features?.length ?? '—'}
              </Typography>
            </Box>
          </Box>
        </Box>

        {previewData && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1300,
              bgcolor: 'white'
            }}>
            <ProfessionalReportPreview
              reportData={previewData}
              onClose={() => setPreviewData(null)}
            />
          </Box>
        )}
      </AdviseAccountGuard>
    </PageLayout>
  );
};
